
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    serverTimestamp,
    doc,
    setDoc,
    getDoc,
    where,
    getDocs,
    writeBatch,
    Timestamp,
} from "firebase/firestore";
import { db, auth } from "../../Confg/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import { FiX, FiSend, FiHeadphones, FiClock, FiMessageCircle } from "react-icons/fi";
import styles from "./SupportWidget.module.css";
import { useUser } from "../../Confg/UserContext";
import { getSupportWebhookUrl } from "../../Confg/appConfig";
import { renovarTokenSePermissaoNegada } from "../../utils/authRetry";
import NinaChat from "./NinaChat";

const SupportWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [currentUser, setCurrentUser] = useState(null);
    const { user: contextUser, merchantData, setUid } = useUser();
    const [chatId, setChatId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [finalizedChats, setFinalizedChats] = useState([]);
    const [isFinalized, setIsFinalized] = useState(false);
    const [notificationPermission, setNotificationPermission] = useState(null);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);
    const [modoHumano, setModoHumano] = useState(false);

    const previousMessagesCountRef = useRef(0);
    const isInitialLoadRef = useRef(true);
    const previousChatIdRef = useRef(null);

    const messagesEndRef = useRef(null);
    const location = useLocation();

    const isCardapioIfood = location.pathname.toLowerCase().includes("cardapio-ifood") || location.pathname.toLowerCase().includes("cardapioifood");
    // Telas do cliente final (cardápio público, rastreio, link curto): sem chat de suporte.
    const isClientScreen = /^\/(merchants\/[^/]+\/cardapio|rastreio|c)(\/|$)/i.test(location.pathname);
    const supportEmail = "carlosfilipedesouza@gmail.com";
    // Vem do settings/appConfig (cache local + 1 leitura), não mais fixo no build.
    const webhookUrl = getSupportWebhookUrl();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser(user);
                setUid(user.uid); // Set uid in context
                // Generate a consistent Chat ID for the user
                setChatId(`support_${user.uid}`);
            } else {
                // Handle guest users if needed, for now just null
                setCurrentUser(null);
                setUid(null);
                // Maybe generate a guest ID?
                // setChatId(`guest_${Date.now()}`);
            }
        });

        return () => unsubscribe();
    }, [setUid]);

    useEffect(() => {
        if ('Notification' in window) {
            setNotificationPermission(Notification.permission);
            if (Notification.permission === 'default') {
                Notification.requestPermission().then(permission => {
                    setNotificationPermission(permission);
                });
            }
        }
    }, []);

    // Permite abrir o chat de suporte de qualquer lugar do app (ex.: banner de
    // limite de crédito no Dashboard) disparando window.dispatchEvent(new CustomEvent('auuii:open-support')).
    useEffect(() => {
        const openHandler = () => setIsOpen(true);
        window.addEventListener('auuii:open-support', openHandler);
        return () => window.removeEventListener('auuii:open-support', openHandler);
    }, []);





    useEffect(() => {
        if (!chatId) return;

        const checkStatus = async () => {
            try {
                const chatDocRef = doc(db, "chats", chatId);
                const chatDoc = await getDoc(chatDocRef);
                if (chatDoc.exists()) {
                    setIsFinalized(chatDoc.data().status === "finalizado");
                } else {
                    setIsFinalized(false);
                }
            } catch (err) {
                // Não derruba o app se as regras negarem a leitura de chats.
                console.warn('[SupportWidget] checkStatus negado:', err?.code || err?.message);
                setIsFinalized(false);
            }
        };

        checkStatus();

        if (previousChatIdRef.current !== chatId) {
            previousMessagesCountRef.current = 0; // Reset for new chat
            isInitialLoadRef.current = true; // Reset initial load flag
            previousChatIdRef.current = chatId;
        }
        setLoading(true);
        const messagesRef = collection(db, "chats", chatId, "messages");
        const q = query(messagesRef, orderBy("timestamp", "asc"));

        // Assinatura isolada numa função para poder REASSINAR: quando o listener falha
        // com `permission-denied` logo após o login (ID token recém-emitido ainda não
        // valendo), o SDK encerra a assinatura. Renovamos o token e assinamos UMA vez
        // mais; sem isso o chat ficava vazio até a pessoa recarregar a página.
        let cancelado = false;
        let unsubscribe = () => {};

        const assinarMensagens = (isRetry = false) => {
            unsubscribe = onSnapshot(q, (snapshot) => {
                const msgs = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                const previousCount = previousMessagesCountRef.current;
                setMessages(msgs);
                setLoading(false);
                scrollToBottom();

                // Show notification for new messages from support when chat is closed, but not on initial load
                if (!isOpen && msgs.length > previousCount && !isInitialLoadRef.current) {
                    const newMessages = msgs.slice(previousCount);
                    newMessages.forEach(msg => {
                        if (msg.senderEmail === supportEmail && notificationPermission === 'granted') {
                            new Notification('Nova mensagem de suporte', {
                                body: msg.message,
                                icon: '/favicon.ico' // or some icon
                            });
                        }
                    });
                }
                previousMessagesCountRef.current = msgs.length;
                isInitialLoadRef.current = false; // After first load, set to false
            }, async (error) => {
                if (!isRetry && await renovarTokenSePermissaoNegada(error)) {
                    if (cancelado) return;
                    unsubscribe();          // o SDK já encerrou no erro; isto só garante
                    assinarMensagens(true);
                    return;
                }
                // Não é fatal: o widget apenas não carrega o histórico de mensagens.
                console.warn('[SupportWidget] mensagens indisponíveis:', error?.code || error?.message);
                setMessages([]);
                setLoading(false);
            });
        };

        assinarMensagens();

        return () => { cancelado = true; unsubscribe(); };
    }, [chatId, isOpen, notificationPermission]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [isOpen, isWaitingResponse]);

    const toggleChat = async () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        // When opening, check if current chat is finalized
        if (nextOpen) {
            if (chatId) {
                try {
                    const chatDocRef = doc(db, "chats", chatId);
                    const chatDoc = await getDoc(chatDocRef);
                    if (chatDoc.exists() && chatDoc.data().status === "finalizado") {
                        // Create a new chat ID
                        const newChatId = `support_${currentUser.uid}_${Date.now()}`;
                        setChatId(newChatId);
                    }
                } catch (err) {
                    console.warn('[SupportWidget] toggleChat getDoc negado:', err?.code || err?.message);
                }
            }
        }
    };

    const fetchFinalizedChats = async () => {
        try {
            const q = query(collection(db, "chats"), where("users", "array-contains", currentUser.email), where("status", "==", "finalizado"));
            const querySnapshot = await getDocs(q);
            const chats = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setFinalizedChats(chats);
        } catch (err) {
            console.warn('[SupportWidget] fetchFinalizedChats negado:', err?.code || err?.message);
            setFinalizedChats([]);
        }
    };

    const handleHistoryClick = () => {
        if (!showHistory) {
            fetchFinalizedChats();
        }
        setShowHistory(!showHistory);
    };

    const openChat = (chatId) => {
        setChatId(chatId);
        setShowHistory(false);
        setModoHumano(true);
    };

    const transferirParaAtendimento = async (conversaNina) => {
        if (!currentUser || !chatId) return;

        const targetChatId = isFinalized
            ? `support_${currentUser.uid}_${Date.now()}`
            : chatId;
        const chatDocRef = doc(db, "chats", targetChatId);
        const chatDoc = await getDoc(chatDocRef);
        const ultimaMensagem = [...conversaNina].reverse().find((msg) => msg.autor !== "erro");
        const batch = writeBatch(db);

        batch.set(chatDocRef, {
            ...(!chatDoc.exists() ? { createdAt: serverTimestamp() } : {}),
            status: "ativo",
            handoff: true,
            users: [currentUser.email, supportEmail],
            ...(ultimaMensagem ? { lastMessage: ultimaMensagem.texto } : {}),
            lastMessageAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        }, { merge: true });

        const inicio = Date.now();
        conversaNina.forEach((msg, index) => {
            if (msg.autor === "erro") return;
            const enviadoPeloUsuario = msg.autor === "eu";
            const messageRef = doc(collection(db, "chats", targetChatId, "messages"));
            batch.set(messageRef, {
                message: msg.texto,
                senderEmail: enviadoPeloUsuario ? currentUser.email : supportEmail,
                senderName: enviadoPeloUsuario
                    ? (merchantData?.name || currentUser.email.split("@")[0])
                    : "Nina",
                recipientEmail: enviadoPeloUsuario ? supportEmail : currentUser.email,
                timestamp: Timestamp.fromMillis(inicio + index),
                path: location.pathname,
            });
        });
        await batch.commit();

        setChatId(targetChatId);
        setIsFinalized(false);
        setShowHistory(false);
        setModoHumano(true);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !chatId) return; // Might allow guest chat?

        const messageText = newMessage;
        setNewMessage(""); // Optimistic clear

        // Prepare message data
        const senderEmail = currentUser ? currentUser.email : "anonymous@user.com";
        const ctxName = contextUser && (contextUser.displayName || contextUser.name);
        const senderName = ctxName || (currentUser ? currentUser.email.split('@')[0] : 'Anonymous');

        const messageData = {
            message: messageText,
            senderEmail,
            senderName,
            recipientEmail: supportEmail,
            timestamp: serverTimestamp(),
            path: location.pathname
        };

        try {
            // Check if the main document exists, if not, create it
            const chatDocRef = doc(db, "chats", chatId);
            const chatDoc = await getDoc(chatDocRef);
            if (!chatDoc.exists()) {
                await setDoc(chatDocRef, {
                    createdAt: serverTimestamp(),
                    status: "ativo",
                    handoff: false,
                    users: [senderEmail, supportEmail],
                    lastMessage: messageText,
                    lastMessageAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            } else {
                // Update lastMessage, lastMessageAt, updatedAt
                await setDoc(chatDocRef, {
                    lastMessage: messageText,
                    lastMessageAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
            }

            // 1. Always save to Firestore
            await addDoc(collection(db, "chats", chatId, "messages"), messageData);

            // 2. Send to Webhook only if handoff is false (not transferred to human)
            const chatDocSnap = await getDoc(chatDocRef);
            const handoff = chatDocSnap.data()?.handoff || false;
            let response = null;
            if (!handoff) {
                setIsWaitingResponse(true);
                response = await axios.post(webhookUrl, {
                    message: messageText,
                    sender: currentUser ? currentUser.email : "Anonymous",
                    userId: currentUser ? currentUser.uid : "anonymous",
                    chatId: chatId,
                    path: location.pathname,
                    timestamp: new Date().toISOString()
                });
            }

            // 3. Save response from Webhook to Firestore
            // 3. Save response from Webhook to Firestore
            if (response.data) {
                let finalMessage = "";
                let handoffValue = false;
                const data = response.data;

                // Helper to extract message and handoff from an object
                const extract = (obj) => {
                    if (!obj) return { message: null, handoff: false };
                    const message = obj.mensagem || obj.message || obj.response || obj.output || (obj.data && typeof obj.data === 'object' ? extract(obj.data).message : null);
                    const handoff = obj.handoff !== undefined ? obj.handoff : false;
                    return { message, handoff };
                };

                // 1. Try direct extraction if object
                if (typeof data === 'object') {
                    const extracted = extract(data);
                    finalMessage = extracted.message;
                    handoffValue = extracted.handoff;
                    if (!finalMessage) finalMessage = JSON.stringify(data);
                }
                // 2. Try parsing if string
                else if (typeof data === 'string') {
                    try {
                        const parsed = JSON.parse(data);
                        if (typeof parsed === 'object') {
                            const extracted = extract(parsed);
                            finalMessage = extracted.message;
                            handoffValue = extracted.handoff;
                        }
                    } catch (e) { }

                    if (!finalMessage) finalMessage = data;
                }

                // --- HOTFIX: If finalMessage is a JSON string containing 'mensagem', parse it again ---
                if (typeof finalMessage === 'string' && finalMessage.trim().startsWith('{') && (finalMessage.includes('"mensagem"') || finalMessage.includes('mensagem'))) {
                    try {
                        const parsed = JSON.parse(finalMessage);
                        const extracted = extract(parsed);
                        if (extracted.message) finalMessage = extracted.message;
                        if (extracted.handoff !== undefined) handoffValue = extracted.handoff;
                    } catch (e) { }
                }

                // Update handoff in chat document if true
                if (handoffValue === true) {
                    await setDoc(chatDocRef, {
                        handoff: true,
                        updatedAt: serverTimestamp(),
                    }, { merge: true });
                }

                // Always save response message
                if (finalMessage && finalMessage !== "OK" && finalMessage !== "{}" && finalMessage !== "undefined") {
                    await addDoc(collection(db, "chats", chatId, "messages"), {
                        message: finalMessage,
                        senderEmail: supportEmail,
                        recipientEmail: currentUser ? currentUser.email : "anonymous",
                        timestamp: serverTimestamp(),
                    });

                    // Update lastMessage for support response
                    await setDoc(chatDocRef, {
                        lastMessage: finalMessage,
                        lastMessageAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                    }, { merge: true });
                }
            }

        } catch (error) {
            console.error("Error sending message:", error);
            if (error.response) {
                console.error("Resposta do webhook (n8n):", error.response.status, error.response.data);
            }
            // Ideally show error to user
        } finally {
            setIsWaitingResponse(false);
        }
    };



    // Se estiver na tela do CardapioIfood, não renderiza o chat
    if (isCardapioIfood || isClientScreen) return null;

    if (!currentUser) return null; // Don't show if not logged in (based on existing Chat logic)

    return (
        <div className={styles.widgetContainer}>
            {isOpen ? (
                <div className={styles.chatWindow} role="dialog" aria-label="Suporte Auuii">
                    <div className={styles.header}>
                        <div className={styles.headerTitle}>
                            <FiHeadphones className={styles.headerIcon} aria-hidden="true" />
                            <h3>Suporte Auuii</h3>
                        </div>
                        <div className={styles.headerActions}>
                            {modoHumano && (
                                <button type="button" onClick={handleHistoryClick} className={styles.iconButton} aria-label="Conversas anteriores" title="Conversas anteriores">
                                    <FiClock size={16} />
                                </button>
                            )}
                            <button type="button" onClick={toggleChat} className={styles.closeButton} aria-label="Fechar suporte">
                                <FiX size={16} />
                            </button>
                        </div>
                    </div>

                    {!modoHumano ? (
                        <NinaChat onHandoffRequested={transferirParaAtendimento} />
                    ) : (
                    <>
                    <div className={styles.messagesArea}>
                        {showHistory ? (
                            <div className={styles.historyView}>
                                <h4>Conversas encerradas</h4>
                                {finalizedChats.length === 0 ? (
                                    <p>Nenhuma conversa encerrada.</p>
                                ) : (
                                    finalizedChats.map((chat) => (
                                        <button type="button" key={chat.id} className={styles.historyItem} onClick={() => openChat(chat.id)}>
                                            <p>{chat.lastMessage}</p>
                                            <span>{chat.lastMessageAt ? new Date(chat.lastMessageAt.toDate()).toLocaleString('pt-BR') : '…'}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        ) : loading ? (
                            <p className={styles.loading}>Carregando…</p>
                        ) : messages.length === 0 ? (
                            <p className={styles.placeholder}>Olá. Como podemos ajudar hoje?</p>
                        ) : (
                            messages.map((msg) => {
                                const isSent = msg.senderEmail === currentUser?.email;
                                const nameToShow = isSent ? (merchantData?.name || 'Você') : (msg.senderName || 'Suporte');
                                return (
                                    <div
                                        key={msg.id}
                                        className={`${styles.message} ${isSent ? styles.sent : styles.received}`}
                                    >
                                        <div className={styles.senderName}>{nameToShow}</div>
                                        <p>{msg.message}</p>
                                        <span className={styles.time}>
                                            {msg.timestamp ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '…'}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                        {!showHistory && !loading && isWaitingResponse && (
                            <div className={styles.typingIndicator} aria-live="polite">Suporte está digitando…</div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {!isFinalized && (
                        <form onSubmit={handleSendMessage} className={styles.inputArea}>
                            <input
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Digite sua mensagem"
                                className={`ap-field-input ${styles.input}`}
                                aria-label="Mensagem"
                            />
                            <button type="submit" className="ap-btn ap-btn-primary">
                                <FiSend size={16} aria-hidden="true" /> Enviar
                            </button>
                        </form>
                    )}
                    </>)}
                </div>
            ) : (
                <button type="button" onClick={toggleChat} className={styles.fabButton} aria-label="Abrir suporte">
                    <FiMessageCircle size={24} />
                </button>
            )}
        </div>
    );
};

export default SupportWidget;
